// Owns the offline library: which books are stored on this device, the
// download queue (one book at a time, one chapter at a time, resumable),
// storage persistence, and the online/offline flag. Audio bytes live in
// OPFS/IndexedDB (storage.js); this context only tracks records.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import wasabiService from '../services/WasabiService';
import { getApiUrl } from '../services/apiConfig';
import { dbGetAll, dbPut, dbDelete } from './db';
import { downloadBook, removeBookFiles, getChapterFile as readChapterFile } from './downloader';
import { bookKeyFor } from './keys';
import { flushPendingPositions } from './positions';
import { isIOS, isStandalone, isOnline, labLog } from './platform';
import InstallSheet from './InstallSheet';
import { noteListened } from './install';
import { resyncPush } from './push';

const OfflineContext = createContext(null);

const STUB = {
  ready: false,
  records: {},
  progress: {},
  online: true,
  standalone: false,
  persisted: null,
  estimate: null,
  requestDownload: async () => false,
  pauseDownload: () => {},
  resumeDownload: () => {},
  removeBook: async () => {},
  getOfflineBook: () => null,
  getChapterFile: async () => null,
  isChapterAvailable: () => false,
  offlineStatusFor: () => ({ status: 'none' }),
  showInstallSheet: () => {},
  noteListened: () => {}
};

export function useOffline() {
  return useContext(OfflineContext) || STUB;
}

const stripForStorage = (book) => {
  // Keep everything the player reads; drop transient/derived fields that
  // JSON cannot hold (functions, blobs) — structured clone handles the rest.
  try {
    return JSON.parse(JSON.stringify(book));
  } catch (e) {
    return book;
  }
};

const summarize = (record) => {
  const total = record.chapters.length;
  const done = record.chapters.filter((c) => c.done).length;
  const bytes = record.chapters.reduce((sum, c) => sum + (Number(c.bytes) || 0), 0);
  return { total, done, bytes };
};

async function fetchCoverBlob(book) {
  try {
    let url = '';
    let revoke = '';
    if (book.isCurated && book.id) {
      url = getApiUrl(`/api/curated-cover/${encodeURIComponent(book.id)}`);
    } else if (book.coverUrl) {
      const raw = String(book.coverUrl);
      if (raw.includes('X-Amz-')) {
        url = raw;
      } else {
        // The resolver returns { src, shouldRevoke }: a blob URL fetched
        // through the backend (storage keys are private).
        const resolved = await wasabiService.resolveCoverImageSource(raw);
        url = typeof resolved === 'string' ? resolved : (resolved?.src || '');
        if (resolved?.shouldRevoke) revoke = url;
      }
    }
    if (!url) return null;
    const response = await fetch(url, url.startsWith('blob:') ? {} : { credentials: 'include' });
    if (revoke) URL.revokeObjectURL(revoke);
    if (!response.ok) return null;
    const blob = await response.blob();
    return blob.size > 0 && blob.size < 5 * 1024 * 1024 ? blob : null;
  } catch (e) {
    return null;
  }
}

export function OfflineProvider({ children }) {
  const authContext = useAuth();
  const { currentUser, getToken } = authContext;
  const [records, setRecords] = useState({});

  // The API client needs the sign-in context for signed requests. Only the
  // library page used to hand it over, so pages opened directly (/downloads,
  // /lab/offline) could not subscribe to push or sync positions.
  useEffect(() => {
    wasabiService.setAuthContext(authContext);
  }, [authContext]);
  const [progress, setProgress] = useState({});
  const [ready, setReady] = useState(false);
  const [online, setOnline] = useState(isOnline());
  const [persisted, setPersisted] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [installSheetOpen, setInstallSheetOpen] = useState(false);
  const [installSheetReason, setInstallSheetReason] = useState('download');
  const standalone = useMemo(() => isStandalone(), []);

  const queueRef = useRef([]);
  const activeRef = useRef(null); // { key, controller }
  const recordsRef = useRef({});
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;

  const commitRecord = useCallback(async (record) => {
    recordsRef.current = { ...recordsRef.current, [record.key]: record };
    setRecords(recordsRef.current);
    try { await dbPut('books', record); } catch (e) { console.warn('Offline: could not save record', e); }
  }, []);

  const setBookProgress = useCallback((key, patch) => {
    setProgress((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), ...patch } }));
  }, []);

  const refreshEstimate = useCallback(async () => {
    try {
      if (navigator.storage?.estimate) {
        const est = await navigator.storage.estimate();
        setEstimate({ usage: est.usage || 0, quota: est.quota || 0 });
      }
    } catch (e) { /* unsupported */ }
  }, []);

  // Load records, ask for persistent storage, read the quota.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const all = await dbGetAll('books');
        if (cancelled) return;
        const map = {};
        for (const record of all) {
          // A download interrupted by a kill/reload resumes on demand.
          if (record.status === 'downloading') record.status = 'paused';
          // Older records could be saved with an empty title.
          if (!record.title) record.title = String(record.book?.title || record.book?.name || record.summary?.name || record.summary?.title || 'Untitled');
          map[record.key] = record;
        }
        recordsRef.current = map;
        setRecords(map);
        const nextProgress = {};
        for (const record of all) {
          const { total, done, bytes } = summarize(record);
          nextProgress[record.key] = { status: record.status, chapterIndex: done, chapterCount: total, bytes };
        }
        setProgress(nextProgress);
        // Records saved before covers were captured correctly: fetch the
        // cover now, once, while online.
        if (isOnline()) {
          for (const record of all) {
            if (cancelled) break;
            if (record.coverBlob || record.status !== 'complete' || !record.book) continue;
            const coverBlob = await fetchCoverBlob({ ...record.book, isCurated: record.isCurated, coverUrl: record.book?.coverUrl || record.summary?.coverUrl });
            if (coverBlob && !cancelled) commitRecord({ ...record, coverBlob });
          }
        }
      } catch (e) {
        console.warn('Offline: could not load records', e);
      } finally {
        if (!cancelled) setReady(true);
      }
      try {
        if (navigator.storage?.persisted) {
          let granted = await navigator.storage.persisted();
          if (!granted && navigator.storage.persist && (standalone || !isIOS())) {
            granted = await navigator.storage.persist();
          }
          if (!cancelled) setPersisted(Boolean(granted));
        }
      } catch (e) { /* unsupported */ }
      refreshEstimate();
    })();
    return () => { cancelled = true; };
  }, [standalone, refreshEstimate, commitRecord]);

  // Online/offline flag; sync positions saved offline when back online.
  useEffect(() => {
    const goOnline = () => {
      setOnline(true);
      if (currentUser) flushPendingPositions(wasabiService).catch(() => {});
    };
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    if (isOnline() && currentUser) {
      flushPendingPositions(wasabiService).catch(() => {});
      // iOS drops push subscriptions silently after reboots/idle: re-send ours.
      resyncPush().catch(() => {});
    }
    const onWorkerMessage = (event) => {
      if (event.data?.type === 'PUSH_RESUBSCRIBE' && currentUser) resyncPush().catch(() => {});
    };
    if (navigator.serviceWorker) navigator.serviceWorker.addEventListener('message', onWorkerMessage);
    return () => {
      if (navigator.serviceWorker) navigator.serviceWorker.removeEventListener('message', onWorkerMessage);
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, [currentUser]);

  const runQueue = useCallback(async () => {
    if (activeRef.current) return;
    const key = queueRef.current.shift();
    if (!key) return;
    const record = recordsRef.current[key];
    if (!record) { runQueue(); return; }

    const controller = new AbortController();
    activeRef.current = { key, controller };
    record.status = 'downloading';
    await commitRecord({ ...record });
    setBookProgress(key, { status: 'downloading', error: null });

    let persistTimer = null;
    const schedulePersist = () => {
      if (persistTimer) return;
      persistTimer = setTimeout(() => { persistTimer = null; commitRecord({ ...record }); }, 3000);
    };

    try {
      await downloadBook({
        record,
        book: record.book,
        userId: currentUser?.uid,
        getToken: (force) => getTokenRef.current(force),
        signal: controller.signal,
        onChapterProgress: (index, entry) => {
          const { bytes } = summarize(record);
          setBookProgress(key, { chapterIndex: index, chapterCount: record.chapters.length, bytes, chapterBytes: entry.bytes, chapterTotal: entry.total });
          schedulePersist();
        },
        onChapterDone: (index) => {
          const { bytes } = summarize(record);
          setBookProgress(key, { chapterIndex: index + 1, chapterCount: record.chapters.length, bytes });
          commitRecord({ ...record });
        }
      });
      record.status = 'complete';
      record.completedAt = Date.now();
      labLog('download complete', { book: record.title, bytes: summarize(record).bytes });
      const { bytes } = summarize(record);
      record.bytes = bytes;
      await commitRecord({ ...record });
      setBookProgress(key, { status: 'complete', chapterIndex: record.chapters.length, chapterCount: record.chapters.length, bytes });
    } catch (error) {
      const aborted = controller.signal.aborted || error?.name === 'AbortError';
      record.status = aborted ? 'paused' : 'error';
      await commitRecord({ ...record });
      setBookProgress(key, { status: record.status, error: aborted ? null : String(error?.message || error) });
      if (!aborted) {
        console.error('Offline download failed:', error);
        labLog('download error', { book: record.title, message: String(error?.message || error), name: error?.name });
      }
    } finally {
      if (persistTimer) clearTimeout(persistTimer);
      activeRef.current = null;
      refreshEstimate();
      runQueue();
    }
  }, [commitRecord, currentUser?.uid, refreshEstimate, setBookProgress]);

  const enqueue = useCallback((key) => {
    if (activeRef.current?.key === key || queueRef.current.includes(key)) return;
    queueRef.current.push(key);
    setBookProgress(key, { status: 'queued', error: null });
    runQueue();
  }, [runQueue, setBookProgress]);

  const showInstallSheet = useCallback((reason = 'banner') => { setInstallSheetReason(reason); setInstallSheetOpen(true); }, []);

  /**
   * Start (or resume) downloading a book. `summary` is the library card item
   * ({ id, name/title, coverUrl, ... }); the full record is fetched here.
   */
  const requestDownload = useCallback(async (summary, isCurated = false) => {
    if (!summary?.id) return false;
    if (isIOS() && !standalone) {
      // Safari-tab storage is separate from the installed app's and is not
      // persistent: get the user onto the Home Screen first.
      setInstallSheetReason('download');
      setInstallSheetOpen(true);
      return false;
    }
    const key = bookKeyFor(summary.id, isCurated);
    const existing = recordsRef.current[key];
    if (existing && existing.status !== 'error') {
      if (existing.status !== 'complete') enqueue(key);
      return true;
    }
    setBookProgress(key, { status: 'preparing', error: null });
    try {
      const book = isCurated
        ? await wasabiService.getCuratedAudiobook(summary.id)
        : await wasabiService.getAudiobook(currentUser.uid, summary.id);
      const chapters = Array.isArray(book?.chapters) && book.chapters.length
        ? book.chapters
        : [{ id: '1', title: 'Chapter 1' }];
      const coverBlob = await fetchCoverBlob({ ...book, isCurated, coverUrl: book?.coverUrl || summary.coverUrl });
      const record = {
        key,
        id: String(summary.id),
        uid: currentUser?.uid || '',
        isCurated: Boolean(isCurated),
        title: String(book?.title || book?.name || summary.name || summary.title || 'Untitled'),
        summary: stripForStorage({ ...summary, isCurated: Boolean(isCurated) }),
        book: stripForStorage({ ...book, isCurated: Boolean(isCurated) }),
        chapters: chapters.map((chapter, index) => ({
          id: String(chapter?.id ?? index + 1),
          title: chapter?.title || `Chapter ${index + 1}`,
          duration: Number(chapter?.duration || 0),
          key: '',
          bytes: 0,
          total: 0,
          done: false
        })),
        coverBlob,
        status: 'queued',
        bytes: 0,
        createdAt: Date.now()
      };
      await commitRecord(record);
      enqueue(key);
      return true;
    } catch (error) {
      console.error('Offline: could not prepare download', error);
      labLog('download prepare error', { book: summary?.name || summary?.title, message: String(error?.message || error) });
      setBookProgress(key, { status: 'error', error: String(error?.message || error) });
      return false;
    }
  }, [commitRecord, currentUser?.uid, enqueue, setBookProgress, standalone]);

  const pauseDownload = useCallback((key) => {
    if (activeRef.current?.key === key) {
      activeRef.current.controller.abort();
      return;
    }
    queueRef.current = queueRef.current.filter((k) => k !== key);
    const record = recordsRef.current[key];
    if (record && record.status !== 'complete') {
      commitRecord({ ...record, status: 'paused' });
      setBookProgress(key, { status: 'paused' });
    }
  }, [commitRecord, setBookProgress]);

  const resumeDownload = useCallback((key) => {
    const record = recordsRef.current[key];
    if (!record || record.status === 'complete') return;
    enqueue(key);
  }, [enqueue]);

  const removeBook = useCallback(async (key) => {
    if (activeRef.current?.key === key) activeRef.current.controller.abort();
    queueRef.current = queueRef.current.filter((k) => k !== key);
    const next = { ...recordsRef.current };
    delete next[key];
    recordsRef.current = next;
    setRecords(next);
    setProgress((prev) => { const copy = { ...prev }; delete copy[key]; return copy; });
    try { await removeBookFiles(key); } catch (e) { console.warn('Offline: could not remove files', e); }
    try { await dbDelete('books', key); } catch (e) { /* already gone */ }
    refreshEstimate();
  }, [refreshEstimate]);

  const getOfflineBook = useCallback((key) => recordsRef.current[key] || null, []);

  const isChapterAvailable = useCallback((key, chapterId) => {
    const record = recordsRef.current[key];
    if (!record) return false;
    const entry = record.chapters.find((c) => String(c.id) === String(chapterId));
    return Boolean(entry && entry.done);
  }, []);

  const getChapterFile = useCallback(async (key, chapterId) => {
    if (!isChapterAvailable(key, chapterId)) return null;
    try { return await readChapterFile(key, chapterId); } catch (e) { return null; }
  }, [isChapterAvailable]);

  const offlineStatusFor = useCallback((bookId, isCurated) => {
    const key = bookKeyFor(bookId, isCurated);
    const record = records[key];
    const p = progress[key];
    if (!record && !p) return { key, status: 'none' };
    const { total, done, bytes } = record ? summarize(record) : { total: 0, done: 0, bytes: 0 };
    return {
      key,
      status: p?.status || record?.status || 'none',
      chapterIndex: p?.chapterIndex ?? done,
      chapterCount: p?.chapterCount ?? total,
      bytes: p?.bytes ?? bytes,
      chapterBytes: p?.chapterBytes || 0,
      chapterTotal: p?.chapterTotal || 0,
      error: p?.error || null
    };
  }, [records, progress]);

  const value = useMemo(() => ({
    ready,
    records,
    progress,
    online,
    standalone,
    persisted,
    estimate,
    requestDownload,
    pauseDownload,
    resumeDownload,
    removeBook,
    getOfflineBook,
    getChapterFile,
    isChapterAvailable,
    offlineStatusFor,
    showInstallSheet,
    noteListened,
    refreshEstimate
  }), [ready, records, progress, online, standalone, persisted, estimate, requestDownload, pauseDownload, resumeDownload, removeBook, getOfflineBook, getChapterFile, isChapterAvailable, offlineStatusFor, showInstallSheet, refreshEstimate]);

  return (
    <OfflineContext.Provider value={value}>
      {children}
      {installSheetOpen && <InstallSheet reason={installSheetReason} onClose={() => setInstallSheetOpen(false)} />}
    </OfflineContext.Provider>
  );
}

export { bookKeyFor };
